import React, { type ComponentType } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { fg } from '@atlassian/jira-feature-gating';
import { PageTopBar } from '@atlassian/jira-horizontal-nav-page-topbar/src/ui/index.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { getWillShowNav3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav3.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import ProjectKeyboardShortcuts from '@atlassian/jira-software-keyboard-shortcuts/src/ui/project-shortcuts/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { Skeleton } from '../common/ui/skeleton/index.tsx';
import { isErrorTrackingEnabledWithSentry, isPollinatorTenant } from '../feature-flags';
import { reportsOverviewPageLoad } from '../services/utils/performance-analytics';
import messages from './messages';

export type Props = {
	isFirstDataLoaded: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	List: ComponentType<Record<any, any>>;
	BreadcrumbRenderer: ComponentType<{}>;
	boardId: number | string | null | undefined;
	baseUrl: string;
	projectKey: string;
};

export type PropsWithIntl = Props & Intl;

export function SoftwareReports({
	List,
	isFirstDataLoaded,
	boardId,
	projectKey,
	intl: { formatMessage },
	BreadcrumbRenderer,
}: PropsWithIntl) {
	const breadcrumbs = <BreadcrumbRenderer />;
	return (
		<Container>
			{getWillShowNav4() && fg('update_jira_software_reports_for_nav4') ? (
				<PageTopBar truncateTitle breadcrumbs={breadcrumbs}>
					{formatMessage(messages.caption)}
				</PageTopBar>
			) : (
				<PageHeader truncateTitle breadcrumbs={breadcrumbs}>
					{formatMessage(messages.caption)}
				</PageHeader>
			)}
			{getWillShowNav3() && <ProjectKeyboardShortcuts boardId={boardId} projectKey={projectKey} />}
			{isFirstDataLoaded ? <List /> : <Skeleton />}
			<ViewTracker />
			{__SPA__ && isFirstDataLoaded && (
				<>
					<SubmitApdex
						appName="software-reports-overview"
						ttrSlo={4000}
						ttiSlo={4000}
						extra={{
							isSentryEnabled: String(isErrorTrackingEnabledWithSentry()),
							synthetic: isPollinatorTenant(),
						}}
						// @ts-expect-error - TS2769 - No overload matches this call.
						metric={reportsOverviewPageLoad}
					/>
					<SpaStatePageReady />
				</>
			)}
		</Container>
	);
}

export default injectIntl(SoftwareReports);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: `0 ${token('space.400', '32px')}`,
});
